import { ArrowRight } from 'lucide-react'
import { useRef, useState } from 'react'
import { useNavigate } from 'react-router'

import { type GrapeAPIErrorType } from 'components/hooks/GrapeAPIError'
import { partnersProspects } from 'rails_routes'
import { ButtonGroup, Modal } from 'slab'
import { useDeleteQuotingProspectsProspectIdUploadsSbcsBlobId, usePostQuotingProspectsProspectIdUploadsSbcs } from 'utils/web_api'
import { type Document, QuotingProspectQuoteRequestStatus } from 'utils/web_api.schemas'
import { useWindowLocation } from 'utils/windowLocation'

import { QuoteRequestStep } from '..'
import { useProspect } from '../ProspectProvider'
import { QuoteRequestUploadPage } from '../QuoteRequestUploadPage'
import { useQuoteRequestProgress } from '../useQuoteRequestProgress'

interface SbcsUploadPageProps {
  accept?: string[]
  returnToProspect?: boolean
}

export function SbcsUploadPage ({ accept, returnToProspect }: SbcsUploadPageProps) {
  const { assign } = useWindowLocation()
  const { prospect, refetch } = useProspect()
  const navigate = useNavigate()
  const { completeStep } = useQuoteRequestProgress({
    step: QuotingProspectQuoteRequestStatus.sbcs,
    onSuccess: () => navigate(`../../${QuoteRequestStep.OTHER_FILES_UPLOAD}`)
  })
  const [isUploading, setUploading] = useState(false)
  const [error, setError] = useState<{ title: string, errors: string[] }>()
  const formDataRef = useRef<FormData>()
  const [isConfirmationDialogOpen, setConfirmationDialogOpen] = useState(false)

  const { mutate: uploadSbcs } = usePostQuotingProspectsProspectIdUploadsSbcs({
    mutation: {
      onSuccess: () => {
        setUploading(false)
        window.segment?.track('SBC(s) upload succeeded', { id: prospect?.id })
        refetch()
      },
      onError: (err: GrapeAPIErrorType) => {
        setUploading(false)
        window.segment?.track('SBC(s) upload failed', { id: prospect?.id, errors: err.errors })
        setError({
          title: err.message,
          errors: err.errors
        })
      }
    },
    request: {
      get body () { return formDataRef.current }
    }
  })

  const { mutate: deleteSbcs } = useDeleteQuotingProspectsProspectIdUploadsSbcsBlobId({
    mutation: {
      onSuccess: () => {
        window.segment?.track('SBC(s) deleted', { id: prospect?.id })
        refetch()
      },
      onError: (err: GrapeAPIErrorType) => {
        setError({
          title: err.message,
          errors: err.errors
        })
      }
    },
  })

  if (!prospect) {
    return null
  }

  const navigateNext = () => {
    returnToProspect
      ? assign(partnersProspects.show({ id: prospect.id }))
      : completeStep()
  }

  const onBack = () => {
    if (returnToProspect) {
      assign(partnersProspects.show({ id: prospect.id }))
    } else {
      navigate(`../../${QuoteRequestStep.CENSUS_UPLOAD}`)
    }
  }
  const onNext = () => {
    if (!prospect.sbcs_uploads?.length) {
      setConfirmationDialogOpen(true)
    } else {
      navigateNext()
    }
  }

  const onUpload = (files: File[]) => {
    setError(undefined)
    const formData = new FormData()
    files.forEach(file => formData.append('files[]', file))
    formDataRef.current = formData
    setUploading(true)
    window.segment?.track('SBC(s) upload attempted', { id: prospect?.id, amount: files.length })
    uploadSbcs({
      prospectId: prospect.id
    })
  }

  const onDelete = (file: Document) => {
    deleteSbcs({ prospectId: prospect.id, blobId: file.id })
  }

  return (
    <QuoteRequestUploadPage
      title='Upload SBC(s)'
      files={prospect?.sbcs_uploads || []}
      accept={accept?.join(',')}
      multiple
      error={error}
      isUploading={isUploading}
      onUpload={onUpload}
      onDelete={onDelete}
      onBack={onBack}
      onNext={onNext}
      nextButtonText={returnToProspect ? 'Return' : undefined}
      canProceed
    >
      <p className='text-gray-500 text-sm'>Please provide us details about {prospect.name}'s current plans. Our team uses this to produce a more accurate quote.</p>

      <Modal
        open={isConfirmationDialogOpen}
        onOpenChange={setConfirmationDialogOpen}
        zIndex={1}
      >
        <Modal.Body>
          <Modal.Title>
            Continue without any SBCs?
          </Modal.Title>
          <Modal.Description>
            Uploading one or multiple SBCs is optional, but if they're available they can help the team produce a more accurate quote.
          </Modal.Description>
        </Modal.Body>
        <Modal.Footer>
          <ButtonGroup>
            <Modal.Button variant='secondary' onClick={() => setConfirmationDialogOpen(false)}>
              Upload an SBC
            </Modal.Button>
            <Modal.Button
              variant='primary'
              rightIcon={ArrowRight}
              onClick={navigateNext}
            >
              Continue without SBCS
            </Modal.Button>
          </ButtonGroup>
        </Modal.Footer>
      </Modal>
    </QuoteRequestUploadPage>
  )
}
